import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  BookOpen,
  Plus,
  Save,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";

const LedgerMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [ledgerName, setLedgerName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [aliasName, setAliasName] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [address3, setAddress3] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [groupOptions, setGroupOptions] = useState([]);
  const [ledgerList, setLedgerList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showForm, setShowForm] = useState(true);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  useEffect(() => {
    fetchGroupOptions();
  }, [commonHeaders]);

  useEffect(() => {
    fetchLedgerList(page);
  }, [page, commonHeaders, branchId]);

  const fetchGroupOptions = async () => {
    try {
      const res = await axios.get("http://nissi-india.com/api/ledger_groupMaster", {
        headers: commonHeaders,
      });

      if (Array.isArray(res.data)) {
        setGroupOptions(res.data);
      } else if (Array.isArray(res.data.data)) {
        setGroupOptions(res.data.data);
      } else {
        setGroupOptions([]);
        toast.error("Unexpected group data format");
      }
    } catch (err) {
      console.error("Group fetch error:", err);
      setGroupOptions([]);
      toast.error("Failed to load group names");
    }
  };

  const fetchLedgerList = async (pg = page) => {
    try {
      if (!branchId) {
        setLedgerList([]);
        toast.warning("Branch ID not found in localStorage");
        return;
      }

      const res = await axios.get("http://nissi-india.com/api/ledgerMaster", {
        params: {
          branchid: branchId,
          page: pg,
          pageSize: limit,
        },
        headers: commonHeaders,
      });

      const data = res.data || {};
      setLedgerList(Array.isArray(data.ledgerList) ? data.ledgerList : []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(
        data.totalPages || Math.ceil((data.totalRecords || 0) / limit) || 1
      );
    } catch (err) {
      console.error("Ledger fetch error:", err);
      setLedgerList([]);
      setTotalRecords(0);
      setTotalPages(1);
      toast.error("Failed to load ledger list");
    }
  };

  const handleAddNew = () => {
    setLedgerName("");
    setGroupId("");
    setAliasName("");
    setOpeningBalance("");
    setAddress1("");
    setAddress2("");
    setAddress3("");
    setEditId(null);
    setSelectedRows([]);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!ledgerName || !groupId || !openingBalance || !address1) {
      toast.warning("Please fill all required fields");
      return;
    }

    if (!branchId || !userId) {
      toast.warning("Branch ID or User ID missing in localStorage");
      return;
    }

    const payload = {
      ledgername: ledgerName,
      aliasname: aliasName,
      branchid: Number(branchId),
      groupid: Number(groupId),
      openingbalance: parseFloat(openingBalance) || 0,
      address1,
      address2,
      address3,
      userid: Number(userId),
    };

    try {
      if (editId) {
        await axios.put(
          `http://nissi-india.com/api/ledgerMaster_update/${editId}`,
          payload,
          { headers: commonHeaders }
        );
        toast.success("Ledger updated successfully");
      } else {
        await axios.post("http://nissi-india.com/api/ledgerMaster_insert", payload, {
          headers: commonHeaders,
        });
        toast.success("Ledger added successfully");
      }

      handleAddNew();
      setPage(1);
      fetchLedgerList(1);
    } catch (err) {
      console.error("Save ledger error:", err);
      toast.error(err?.response?.data?.message || "Failed to save ledger");
    }
  };

  const handleSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("No ledger selected");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSelected = async () => {
    setShowDeleteConfirm(false);

    try {
      await axios.delete("http://nissi-india.com/api/ledgerMaster_delete", {
        data: { ids: selectedRows },
        headers: commonHeaders,
      });

      toast.success("Selected ledger(s) deleted successfully");
      setSelectedRows([]);
      setPage(1);
      fetchLedgerList(1);
    } catch (err) {
      console.error("Delete ledger error:", err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to delete selected ledger(s)"
      );
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await axios.get(
        `http://nissi-india.com/api/ledgerMaster_view/${id}`,
        { headers: commonHeaders }
      );

      const data = res.data?.data;

      if (data) {
        setEditId(data.ledgerid);
        setLedgerName(data.ledgername || "");
        setAliasName(data.aliasname || "");
        setGroupId(data.groupid || "");
        setOpeningBalance(data.openingbalance || "");
        setAddress1(data.address1 || "");
        setAddress2(data.address2 || "");
        setAddress3(data.address3 || "");
        setShowForm(true);
      }
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error("Failed to fetch ledger details");
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Ledger Master</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3
                  className={`text-lg font-bold ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  {editId ? "Edit Ledger" : "Add New Ledger"}
                </h3>
                <p
                  className={`text-xs ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  {editId
                    ? "Update ledger account details"
                    : "Create a new ledger account"}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowForm(!showForm)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {showForm ? "Hide Form" : "Show Form"}
            </button>
          </div>

          {showForm && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div>
                  <label className={labelClass}>
                    Ledger Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={ledgerName}
                    onChange={(e) => setLedgerName(e.target.value)}
                    placeholder="Enter ledger name"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                  >
                    <option value="">Select Group</option>
                    {Array.isArray(groupOptions) &&
                      groupOptions.map((g) => (
                        <option key={g.groupid} value={g.groupid}>
                          {g.groupname}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Alias Name</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={aliasName}
                    onChange={(e) => setAliasName(e.target.value)}
                    placeholder="Optional alias"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Opening Balance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    className={inputClass}
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </div>

                <div>
                  <label className={labelClass}>Address 2</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className={labelClass}>Address 3</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address3}
                    onChange={(e) => setAddress3(e.target.value)}
                    placeholder="Address line 3"
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} />
                  Add New
                </button>

                <button
                  onClick={handleSave}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <Save size={16} />
                  {editId ? "Update Ledger" : "Save Ledger"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-xl font-bold">Ledger List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} records
                </span>
              </div>

              <button
                onClick={handleDeleteClick}
                disabled={selectedRows.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
                  selectedRows.length > 0
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                <Trash2 size={16} />
                Delete ({selectedRows.length})
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Ledger Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Alias Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Group Name
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                    Opening Balance
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    Select
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    Edit
                  </th>
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {Array.isArray(ledgerList) && ledgerList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <BookOpen size={40} className="text-gray-400" />
                        <span>No ledger records found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  ledgerList.map((item, index) => {
                    const isSelected = selectedRows.includes(item.ledgerid);

                    return (
                      <tr
                        key={item.ledgerid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${
                          isSelected
                            ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20`
                            : ""
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className="px-4 py-3 font-medium text-blue-600">
                          {item.ledgername}
                        </td>
                        <td className="px-4 py-3">{item.aliasname || "-"}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.groupname}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          ₹
                          {Number(item.openingbalance || 0).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                            }
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelect(item.ledgerid)}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(item.ledgerid)}
                            className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Showing{" "}
                <span className="font-semibold">
                  {totalRecords === 0 ? 0 : (page - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(page * limit, totalRecords)}
                </span>{" "}
                of <span className="font-semibold">{totalRecords}</span> results
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                {[...Array(Math.min(totalPages, 5))].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      page === idx + 1
                        ? `bg-${baseColor}-${baseShade} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages || totalPages === 0}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === totalPages || totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <ConfirmModal
        open={showDeleteConfirm}
        title="Delete Ledgers"
        message={`Are you sure you want to delete ${selectedRows.length} ledger(s)? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDeleteSelected}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </LayoutWrapper>
  );
};

export default LedgerMaster;